/**
 * PricingSliderSettings — CMS controls that only apply when the Pricing
 * section style is set to "Slider (new)". Surface lives inside PricingTab.
 *
 * Edits the following branches of `content.pricing`:
 *   - slider_tiers.{weekly,monthly,half,season}.{label,desc}
 *   - founders_cap.{name, tagline, price_eur, founders_total, founders_claimed}
 *   - slider_ball_url
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Upload, X, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_BALL_URL = 'https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/jyfl98do_Untitled%20design%20-%202026-06-12T185108.908.png';

const DEFAULT_TIERS = {
  weekly:  { label: 'One Game at a Time', desc: 'For less than the price of a pie & Bovril.' },
  monthly: { label: 'Tournaments',         desc: 'Cup run on? Lock it in for a month.' },
  quarter: { label: 'Pre-Season',          desc: 'A 7\u201312 week block. Perfect for pre-season prep.' },
  half:    { label: 'Underage Seasons',    desc: 'Built around school terms & U12\u2013U16 cycles.' },
  season:  { label: 'Full Season',         desc: 'Pre-season to play-offs. The whole campaign.' },
};

const DEFAULT_CAP = {
  name: "Founders' Cap",
  headline: 'In from day one.',
  tagline: "Be in from day one. Your Founders' Cap locks today's price forever \u2014 and earns you a new Cap every contract anniversary.",
  price_eur: 59.99,
  founders_total: 100,
  founders_claimed: 23,
  features: [],
  cta_text: 'Claim my {name} \u2014 {price}',
  cta_subtext: 'One-time payment \u00b7 A new Cap every contract anniversary \u00b7 Price-locked forever',
};

const DEFAULT_BREAKS = {
  weekly: 1,
  monthly: 6,
  quarter: 12,
  half: 26,
};

const TIER_ORDER = [
  { key: 'weekly',  accent: 'text-blue-500'    },
  { key: 'monthly', accent: 'text-cyan-500'    },
  { key: 'quarter', accent: 'text-cyan-700'    },
  { key: 'half',    accent: 'text-emerald-500' },
  { key: 'season',  accent: 'text-amber-500'   },
];

export function PricingSliderSettings({ content, setContent }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const mode = content.pricing?.mode || 'cards';
  const tiers = content.pricing?.slider_tiers || {};
  const breaksRaw = content.pricing?.slider_tier_breaks || {};
  const cap = content.pricing?.founders_cap || {};
  const ballUrl = content.pricing?.slider_ball_url;

  // Clamp boundaries so they remain strictly increasing and within 1-52.
  const tier1Max = Math.max(1, Math.min(52, Number(breaksRaw.weekly)  || DEFAULT_BREAKS.weekly));
  const tier2Max = Math.max(tier1Max + 1, Math.min(52, Number(breaksRaw.monthly) || DEFAULT_BREAKS.monthly));
  const tier3Max = Math.max(tier2Max + 1, Math.min(52, Number(breaksRaw.quarter) || DEFAULT_BREAKS.quarter));
  const tier4Max = Math.max(tier3Max + 1, Math.min(52, Number(breaksRaw.half)    || DEFAULT_BREAKS.half));

  // Derive the display range for each tier card from current break values.
  const rangeFor = (start, end) => start === end ? `Week ${start}` : `Weeks ${start}\u2013${end}`;
  const ranges = {
    weekly:  rangeFor(1, tier1Max),
    monthly: rangeFor(tier1Max + 1, tier2Max),
    quarter: rangeFor(tier2Max + 1, tier3Max),
    half:    rangeFor(tier3Max + 1, tier4Max),
    season:  rangeFor(tier4Max + 1, 52),
  };

  const patchPricing = (partial) => setContent(prev => ({
    ...prev,
    pricing: { ...(prev.pricing || {}), ...partial },
  }));

  const patchTier = (tierKey, field, value) => setContent(prev => ({
    ...prev,
    pricing: {
      ...(prev.pricing || {}),
      slider_tiers: {
        ...(prev.pricing?.slider_tiers || {}),
        [tierKey]: { ...(prev.pricing?.slider_tiers?.[tierKey] || {}), [field]: value },
      },
    },
  }));

  const patchCap = (field, value) => setContent(prev => ({
    ...prev,
    pricing: {
      ...(prev.pricing || {}),
      founders_cap: { ...(prev.pricing?.founders_cap || {}), [field]: value },
    },
  }));

  const patchBreak = (key, value) => {
    const v = parseInt(value, 10);
    if (Number.isNaN(v)) return;
    setContent(prev => ({
      ...prev,
      pricing: {
        ...(prev.pricing || {}),
        slider_tier_breaks: { ...(prev.pricing?.slider_tier_breaks || {}), [key]: v },
      },
    }));
  };

  const resetTierDefaults = () => {
    patchPricing({ slider_tiers: DEFAULT_TIERS, slider_tier_breaks: DEFAULT_BREAKS });
    toast.success('Tier boundaries, labels & descriptions reset to defaults');
  };

  const resetCapDefaults = () => {
    patchPricing({ founders_cap: DEFAULT_CAP });
    toast.success("Founders' Cap reset to defaults");
  };

  const handleBallUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Image must be under 4MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('section', 'pricing_slider_ball');
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      patchPricing({ slider_ball_url: res.data.image_url });
      toast.success('Ball image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const resetBall = () => {
    patchPricing({ slider_ball_url: '' });
    toast.success('Ball image reverted to default Tango');
  };

  return (
    <Card data-testid="pricing-slider-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Sign Your Contract — Slider Fields
          {mode === 'slider' ? (
            <span className="text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500 text-white">Active</span>
          ) : (
            <span className="text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full bg-gray-300 text-gray-600">Inactive</span>
          )}
        </CardTitle>
        <CardDescription>
          These fields drive the new slider section. They are saved regardless of which mode is active, so you can prepare copy ahead of flipping the toggle above.
          Preview live edits at <code className="text-xs bg-gray-100 px-1 rounded">/pricing-slider-mockup</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">

        {/* === Pre-engagement intro copy === */}
        <section className="pt-2">
          <Label className="text-base font-semibold block mb-1">Pre-engagement Intro Copy</Label>
          <p className="text-xs text-gray-500 mb-3">Shown in the tier badge + description on page load, before the visitor touches the slider. Once they engage, the real tier copy kicks in for the rest of their session.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Intro badge text</Label>
              <Input
                value={content.pricing?.slider_intro_label || ''}
                onChange={(e) => patchPricing({ slider_intro_label: e.target.value })}
                placeholder="Pick a Plan"
                className="mt-1"
                data-testid="intro-label-input"
              />
            </div>
            <div>
              <Label className="text-xs">Intro description</Label>
              <Input
                value={content.pricing?.slider_intro_desc || ''}
                onChange={(e) => patchPricing({ slider_intro_desc: e.target.value })}
                placeholder="Drag the ball to find your perfect plan."
                className="mt-1"
                data-testid="intro-desc-input"
              />
            </div>
          </div>
        </section>

        {/* === Pricing grid headings + Sign CTA === */}
        <section className="pt-6 border-t border-gray-200">
          <Label className="text-base font-semibold block mb-1">Pricing Grid Labels &amp; Sign Button</Label>
          <p className="text-xs text-gray-500 mb-3">Headings shown above the three price tiles, plus the green CTA button below the slider.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs">&quot;Per week&quot; heading</Label>
              <Input
                value={content.pricing?.slider_per_week_label || ''}
                onChange={(e) => patchPricing({ slider_per_week_label: e.target.value })}
                placeholder="Per week"
                className="mt-1"
                data-testid="per-week-label-input"
              />
            </div>
            <div>
              <Label className="text-xs">&quot;Total contract&quot; heading</Label>
              <Input
                value={content.pricing?.slider_total_label || ''}
                onChange={(e) => patchPricing({ slider_total_label: e.target.value })}
                placeholder="Total contract"
                className="mt-1"
                data-testid="total-label-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Sign-up button text</Label>
            <Input
              value={content.pricing?.slider_sign_cta_template || ''}
              onChange={(e) => patchPricing({ slider_sign_cta_template: e.target.value })}
              placeholder={'Sign for {weeks} \u2014 {price}'}
              className="mt-1"
              data-testid="sign-cta-template-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Use <code className="bg-gray-100 px-1 rounded">{'{weeks}'}</code> for the contract length (e.g. <em>&quot;13 weeks&quot;</em>) and <code className="bg-gray-100 px-1 rounded">{'{price}'}</code> for the live total (e.g. <em>&quot;&euro;25.86&quot;</em>). Both placeholders update as the slider moves.
            </p>
          </div>

          {/* Currency switcher position — admin chooses whether the
              EUR/GBP/USD toggle renders above or below the pricing card. */}
          <div className="mt-4">
            <Label className="text-xs">Currency switcher position</Label>
            <select
              value={content.pricing?.currency_switcher_position || 'above'}
              onChange={(e) => patchPricing({ currency_switcher_position: e.target.value })}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="currency-switcher-position-select"
            >
              <option value="above">Above pricing card (default)</option>
              <option value="below">Below pricing card</option>
            </select>
            <p className="text-[11px] text-gray-500 mt-1">
              Controls whether the EUR / GBP / USD buttons render above the pricing card (under the subtitle) or below it.
            </p>
          </div>
        </section>

        {/* === Per-tier labels + descriptions === */}
        <section className="pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="text-base font-semibold">Tier Boundaries, Labels &amp; Descriptions</Label>
              <p className="text-xs text-gray-500 mt-0.5">Choose where each tier ends, then customise the badge label and one-liner shown above the weeks counter.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={resetTierDefaults} data-testid="reset-tiers-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to defaults
            </Button>
          </div>

          {/* Boundary inputs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div>
              <Label className="text-xs">Tier 1 ends at week</Label>
              <Input
                type="number"
                min="1"
                max="49"
                value={tier1Max}
                onChange={(e) => patchBreak('weekly', e.target.value)}
                className="mt-1"
                data-testid="tier-break-weekly-input"
              />
            </div>
            <div>
              <Label className="text-xs">Tier 2 ends at week</Label>
              <Input
                type="number"
                min={tier1Max + 1}
                max="50"
                value={tier2Max}
                onChange={(e) => patchBreak('monthly', e.target.value)}
                className="mt-1"
                data-testid="tier-break-monthly-input"
              />
            </div>
            <div>
              <Label className="text-xs">Tier 3 ends at week</Label>
              <Input
                type="number"
                min={tier2Max + 1}
                max="51"
                value={tier3Max}
                onChange={(e) => patchBreak('quarter', e.target.value)}
                className="mt-1"
                data-testid="tier-break-quarter-input"
              />
            </div>
            <div>
              <Label className="text-xs">Tier 4 ends at week</Label>
              <Input
                type="number"
                min={tier3Max + 1}
                max="51"
                value={tier4Max}
                onChange={(e) => patchBreak('half', e.target.value)}
                className="mt-1"
                data-testid="tier-break-half-input"
              />
            </div>
            <p className="col-span-2 md:col-span-4 text-[11px] text-gray-500 -mt-1">
              Tier 5 (Full Season) automatically covers the rest of the slider. Boundaries must stay in increasing order &mdash; values are clamped automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TIER_ORDER.map(({ key, accent }) => {
              const tier = { ...DEFAULT_TIERS[key], ...(tiers[key] || {}) };
              return (
                <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2" data-testid={`tier-card-${key}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${accent}`}>{ranges[key]}</span>
                  </div>
                  <div>
                    <Label className="text-xs">Tier label</Label>
                    <Input
                      value={tier.label || ''}
                      onChange={(e) => patchTier(key, 'label', e.target.value)}
                      placeholder={DEFAULT_TIERS[key].label}
                      className="mt-1"
                      data-testid={`tier-${key}-label-input`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tier description</Label>
                    <Input
                      value={tier.desc || ''}
                      onChange={(e) => patchTier(key, 'desc', e.target.value)}
                      placeholder={DEFAULT_TIERS[key].desc}
                      className="mt-1"
                      data-testid={`tier-${key}-desc-input`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* === Founders' Cap === */}
        <section className="pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="text-base font-semibold">Founders&apos; Cap (Lifetime Tier)</Label>
              <p className="text-xs text-gray-500 mt-0.5">The gold tile in the top-right corner of the slider card. Tapping it flips the card into a one-time payment offer.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={resetCapDefaults} data-testid="reset-cap-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to defaults
            </Button>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tier name</Label>
                <Input
                  value={cap.name ?? ''}
                  onChange={(e) => patchCap('name', e.target.value)}
                  placeholder={DEFAULT_CAP.name}
                  className="mt-1"
                  data-testid="cap-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">One-time price (EUR base)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={cap.price_eur ?? ''}
                  onChange={(e) => patchCap('price_eur', parseFloat(e.target.value) || 0)}
                  placeholder={String(DEFAULT_CAP.price_eur)}
                  className="mt-1"
                  data-testid="cap-price-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">Auto-converted to GBP/USD using your currency rates above.</p>
              </div>
            </div>

            <div>
              <Label className="text-xs">Headline (large text)</Label>
              <Input
                value={cap.headline ?? ''}
                onChange={(e) => patchCap('headline', e.target.value)}
                placeholder={DEFAULT_CAP.headline}
                className="mt-1"
                data-testid="cap-headline-input"
              />
            </div>

            <div>
              <Label className="text-xs">Tagline (shown in the Cap state)</Label>
              <Textarea
                value={cap.tagline ?? ''}
                onChange={(e) => patchCap('tagline', e.target.value)}
                placeholder={DEFAULT_CAP.tagline}
                rows={2}
                className="mt-1"
                data-testid="cap-tagline-input"
              />
            </div>

            {/* Features list — inherits the Lifetime Season Ticket plan card
                benefits by default so Founders' Cap buyers see everything
                they get. Admin edits with one feature per line; HTML allowed
                (e.g. <strong>bold</strong>). */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <Label className="text-xs">Features / benefits (one per line, HTML allowed)</Label>
                <button
                  type="button"
                  onClick={() => {
                    const lifetimePlan = (content.pricing?.plans || []).find(
                      (p) => p.id === 'lifetime' || (p.name || '').toLowerCase().includes('lifetime'),
                    );
                    const src = Array.isArray(lifetimePlan?.features) ? lifetimePlan.features : [];
                    patchCap('features', [...src]);
                    toast.success(`Loaded ${src.length} feature${src.length === 1 ? '' : 's'} from the Lifetime plan card`);
                  }}
                  className="text-[10px] font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700 underline"
                  data-testid="cap-features-inherit-btn"
                >
                  Load from Lifetime plan
                </button>
              </div>
              <Textarea
                value={Array.isArray(cap.features) ? cap.features.join('\n') : ''}
                onChange={(e) => {
                  const lines = e.target.value.split('\n').map((s) => s.trimEnd()).filter((s) => s.length > 0);
                  patchCap('features', lines);
                }}
                placeholder={"Everything in the current app\nEvery feature we ever ship\nPrice locked forever"}
                rows={6}
                className="mt-1 font-mono text-xs"
                data-testid="cap-features-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Left blank? The Founders&apos; Cap card falls back to the Lifetime plan&apos;s features automatically.
              </p>
            </div>

            {/* CTA button text + subtext — editable copy for the big gold
                button and the small line beneath it. Both support the
                {'{name}'} and {'{price}'} placeholders for auto-substitution. */}
            <div className="pt-3 border-t border-amber-200/60">
              <Label className="text-xs">CTA button text</Label>
              <Input
                value={cap.cta_text ?? ''}
                onChange={(e) => patchCap('cta_text', e.target.value)}
                placeholder={DEFAULT_CAP.cta_text}
                className="mt-1"
                data-testid="cap-cta-text-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Placeholders: <code>{'{name}'}</code> = tier name (e.g. &ldquo;Founders&apos; Cap&rdquo;), <code>{'{price}'}</code> = converted one-time price (e.g. &ldquo;&euro;59.99&rdquo;).
              </p>
            </div>

            <div>
              <Label className="text-xs">CTA subtext (small line below the button, HTML allowed)</Label>
              <Textarea
                value={cap.cta_subtext ?? ''}
                onChange={(e) => patchCap('cta_subtext', e.target.value)}
                placeholder={DEFAULT_CAP.cta_subtext}
                rows={2}
                className="mt-1"
                data-testid="cap-cta-subtext-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Same <code>{'{name}'}</code> / <code>{'{price}'}</code> placeholders apply. Basic HTML supported (e.g. <code>&lt;strong&gt;</code>).
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Total Founders&apos; Caps available</Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={cap.founders_total ?? ''}
                  onChange={(e) => patchCap('founders_total', parseInt(e.target.value, 10) || 0)}
                  placeholder={String(DEFAULT_CAP.founders_total)}
                  className="mt-1"
                  data-testid="cap-founders-total-input"
                />
              </div>
              <div>
                <Label className="text-xs">Already claimed</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={cap.founders_claimed ?? ''}
                  onChange={(e) => patchCap('founders_claimed', parseInt(e.target.value, 10) || 0)}
                  placeholder={String(DEFAULT_CAP.founders_claimed)}
                  className="mt-1"
                  data-testid="cap-founders-claimed-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Drives the scarcity bar. Bump this manually as orders come in (auto-wire is parked in the gamification backlog).
                </p>
              </div>
            </div>

            {/* Sticky scarcity band — surfaces once visitor scrolls past
                pricing. Off by default. */}
            <div className="mt-4 pt-4 border-t border-gray-200" data-testid="founders-sticky-band-controls">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <Label className="text-sm font-semibold">Sticky scarcity band</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Pinned to the bottom of the viewport once the visitor scrolls past Pricing. Hides at the footer.</p>
                </div>
                <input
                  type="checkbox"
                  checked={cap.show_sticky_band === true}
                  onChange={(e) => patchCap('show_sticky_band', e.target.checked)}
                  className="h-5 w-9 rounded-full appearance-none bg-gray-300 checked:bg-[#007a33] relative cursor-pointer transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
                  data-testid="founders-sticky-band-toggle"
                />
              </div>

              {cap.show_sticky_band === true && (
                <div className="space-y-3 pl-1">
                  <div>
                    <Label className="text-xs">Count source</Label>
                    <select
                      value={cap.count_mode || 'live'}
                      onChange={(e) => patchCap('count_mode', e.target.value)}
                      className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="founders-band-count-mode"
                    >
                      <option value="live">Live ({Math.max(0, (cap.founders_total ?? DEFAULT_CAP.founders_total) - (cap.founders_claimed ?? 0))} caps left)</option>
                      <option value="manual">Manual override</option>
                    </select>
                  </div>
                  {cap.count_mode === 'manual' && (
                    <div>
                      <Label className="text-xs">Manual count displayed</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={cap.manual_count ?? ''}
                        onChange={(e) => patchCap('manual_count', parseInt(e.target.value, 10) || 0)}
                        placeholder="50"
                        className="mt-1"
                        data-testid="founders-band-manual-count"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Band copy ({'{count}'} is replaced with the number)</Label>
                    <Input
                      value={cap.band_copy || ''}
                      onChange={(e) => patchCap('band_copy', e.target.value)}
                      placeholder="🧢 Only {count} Founders' Caps left — lock lifetime access."
                      className="mt-1"
                      data-testid="founders-band-copy"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Band button text</Label>
                    <Input
                      value={cap.band_cta_text || ''}
                      onChange={(e) => patchCap('band_cta_text', e.target.value)}
                      placeholder="Claim mine"
                      className="mt-1"
                      data-testid="founders-band-cta-text"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* === Ball thumb image === */}
        <section className="pt-6 border-t border-gray-200">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <Label className="text-base font-semibold">Slider Thumb (Ball Image)</Label>
              <p className="text-xs text-gray-500 mt-0.5">
                The ball that rolls along the slider. Default is the Adidas Tango. Upload a custom ball (PNG with transparent background works best, square aspect ratio).
              </p>
            </div>
            <div className="flex gap-2">
              {ballUrl && (
                <Button type="button" variant="outline" size="sm" onClick={resetBall} data-testid="reset-ball-btn">
                  <X className="w-3.5 h-3.5 mr-1" /> Reset
                </Button>
              )}
              <Button type="button" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-ball-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading\u2026' : 'Upload'}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleBallUpload} className="hidden" />
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-emerald-700 to-emerald-900 p-6 flex items-center justify-center" data-testid="ball-preview">
            <div
              className="w-20 h-20 rounded-full bg-cover bg-center shadow-lg shadow-black/40"
              style={{ backgroundImage: `url(${ballUrl || DEFAULT_BALL_URL})` }}
            />
          </div>
          <p className="text-[10px] text-gray-500 mt-2 break-all">
            Current URL: <code className="bg-gray-100 px-1 rounded">{ballUrl || `${DEFAULT_BALL_URL.slice(0, 60)}\u2026 (default)`}</code>
          </p>
        </section>
      </CardContent>
    </Card>
  );
}

export default PricingSliderSettings;
